import { useState, useEffect } from 'react';

import { FileText, Table } from 'lucide-react';

import { Icon } from './icon';
import { Button } from './shadcn/button';
import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import Modal from '@/components/molecules/Modals/SettingsModal';
import { DownloadTableModal } from '@/new/components/download-table';
import { DownloadWrittenModal } from '@/new/components/download-written';

const DownloadFormatSelector = ({ onSelectFormat }: { onSelectFormat: (format: StyleFormat) => void }) => {
  const allFormats = [
    {
      id: StyleFormat.Table,
      label: 'Table',
      icon: <Table />,
      color: 'text-green-600',
      text: 'Word or Excel',
    },
    {
      id: StyleFormat.Written,
      label: 'Written',
      icon: <FileText />,
      color: 'text-blue-600',
      text: 'Word',
    },
  ];

  return (
    <div className="flex flex-row justify-center gap-6 rounded-xl bg-gray-50 p-6 shadow-md">
      {allFormats.map((format) => (
        <div
          key={format.id}
          className={`group flex w-64 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-gray-200 bg-white px-8 py-6 transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-xl`}
          onClick={() => onSelectFormat(format.id)}
        >
          <div
            className={`mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-blue-100 text-4xl text-blue-600 transition-colors duration-200 group-hover:bg-blue-200`}
          >
            {format.icon}
          </div>
          <span className="mb-1 text-lg font-semibold text-gray-800">{format.label}</span>
          <span className="text-center text-sm text-gray-500">{format.text}</span>
        </div>
      ))}
    </div>
  );
};

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalTitle: string;
  downloadTitle: string;
  isLoading: boolean;
  columnOptions: string[];
  onDownloadTable: (params: { title: string; columns: string[]; downloadFormat: DownloadFormat }) => void;
  onDownloadWritten: (params: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
  }) => void;
  displayOptions?: {
    showStyle?: boolean;
    showDocumentOptions?: boolean;
  };
}

export const DownloadModal = ({
  isOpen,
  onClose,
  modalTitle,
  downloadTitle,
  isLoading,
  columnOptions,
  onDownloadTable,
  onDownloadWritten,
  displayOptions = {
    showStyle: true,
    showDocumentOptions: false,
  },
}: DownloadModalProps) => {
  const [selectedFormat, setSelectedFormat] = useState<StyleFormat | null>(null);

  const handleClose = () => {
    onClose();
    // Reset format after modal close animation
    setTimeout(() => setSelectedFormat(null), 300);
  };

  const handleDownloadTableWithClose = (params: {
    title: string;
    columns: string[];
    downloadFormat: DownloadFormat;
  }) => {
    onDownloadTable(params);
  };

  const handleDownloadWrittenWithClose = (params: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
  }) => {
    onDownloadWritten(params);
  };

  // Reset format when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-4 font-semibold text-gray-700">
          {selectedFormat === null ? (
            <>
              <Icon className="lucide--download" />
              {modalTitle}
            </>
          ) : (
            <>
              <Button onClick={() => setSelectedFormat(null)} size="icon" className="rounded-full p-1">
                <Icon className="lucide--arrow-left" />
              </Button>
              {modalTitle} - {selectedFormat === StyleFormat.Table ? 'Table' : 'Written'}
            </>
          )}
        </div>
      }
      isOpen={isOpen}
      handleClose={handleClose}
      content={
        selectedFormat === null ? (
          <DownloadFormatSelector onSelectFormat={setSelectedFormat} />
        ) : selectedFormat === StyleFormat.Table ? (
          <DownloadTableModal
            columnOptions={columnOptions}
            downloadTitle={downloadTitle}
            loadingDownload={isLoading}
            handleDownload={handleDownloadTableWithClose}
          />
        ) : (
          <DownloadWrittenModal
            downloadTitle={downloadTitle}
            loadingDownload={isLoading}
            handleDownload={handleDownloadWrittenWithClose}
            showStyle={displayOptions.showStyle}
            showDocumentOptions={displayOptions.showDocumentOptions}
          />
        )
      }
      size="small"
    />
  );
};
